import React, { useState, useEffect, useRef, useCallback, memo } from 'react';

interface ReplyModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (content: string) => Promise<void>;
    darkMode: boolean;
    parentCommentPreview?: string;
    parentAuthor?: string;
}

const ReplyModal: React.FC<ReplyModalProps> = memo(({
    isOpen,
    onClose,
    onSubmit,
    darkMode,
    parentCommentPreview,
    parentAuthor
}) => {
    const [content, setContent] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setContent('');
            setError('');
            setTimeout(() => textareaRef.current?.focus(), 100);
        }
    }, [isOpen]);

    const handleSubmit = useCallback(async () => {
        if (!content.trim()) return;

        setSubmitting(true);
        setError('');
        try {
            await onSubmit(content);
            setContent('');
            onClose();
        } catch (err: any) {
            setError(err.message || 'Gagal mengirim balasan');
        } finally {
            setSubmitting(false);
        }
    }, [content, onSubmit, onClose]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleSubmit();
        }
    }, [handleSubmit]);

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div
                className={`w-full max-w-lg rounded-lg shadow-xl ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className={`px-6 py-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="flex items-center justify-between">
                        <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            Balas Komentar
                        </h3>
                        <button
                            onClick={onClose}
                            className={`p-2 rounded-full transition-colors ${darkMode
                                ? 'hover:bg-gray-700 text-gray-400'
                                : 'hover:bg-gray-100 text-gray-500'
                                }`}
                        >
                            <i className="fa-solid fa-times"></i>
                        </button>
                    </div>

                    {/* Preview of parent comment */}
                    {parentCommentPreview && (
                        <div className={`mt-3 p-3 rounded-lg text-sm ${darkMode
                            ? 'bg-gray-700/50 border border-gray-600'
                            : 'bg-gray-50 border border-gray-200'
                            }`}>
                            <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                Membalas {parentAuthor || 'komentar'}:
                            </p>
                            <p className={`italic ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                "{parentCommentPreview.length > 100
                                    ? parentCommentPreview.substring(0, 100) + '...'
                                    : parentCommentPreview
                                }"
                            </p>
                        </div>
                    )}
                </div>

                {/* Body */}
                <div className="p-6">
                    <textarea
                        ref={textareaRef}
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Tulis balasan Anda... (Ctrl+Enter untuk kirim)"
                        rows={4}
                        className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none text-sm ${darkMode
                            ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                            }`}
                    />

                    {error && (
                        <div className={`mt-3 p-3 rounded-lg text-sm ${darkMode
                            ? 'bg-red-900/30 border border-red-700 text-red-300'
                            : 'bg-red-50 border border-red-200 text-red-700'
                            }`}>
                            {error}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className={`px-6 py-4 border-t flex justify-end space-x-3 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <button
                        onClick={onClose}
                        disabled={submitting}
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${darkMode
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                    >
                        Batal
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={!content.trim() || submitting}
                        className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        {submitting ? (
                            <>
                                <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                                Mengirim...
                            </>
                        ) : (
                            <>
                                <i className="fa-solid fa-paper-plane mr-2"></i>
                                Kirim Balasan
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
});

ReplyModal.displayName = 'ReplyModal';

export default ReplyModal;
